import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import { BottomNextButton } from "components/common/BottomNextButton";
import { useQuery } from "@tanstack/react-query";
import {
  ONBOARDING_ANALYSIS_DETAIL_ENDPOINT,
  ONBOARDING_DOMAIN,
  onboardingApi,
  SurveyAnalysisDetailItem,
} from "api/onboardingApi";
import { useNavigate } from "react-router-dom";
import { userAtom } from "../../../../recoil/userAtom";
import { useRecoilValue } from "recoil";
import { axiosInstance } from "../../../../api";
import ContentTemplateV2 from "../../../../components/task/content/ContentTemplateV2";
import useGetDeviceInfo from "../../../../hooks/useGetDeviceInfo";
import useMixpanelWeb from "../../../../hooks/useMixpanelWeb";

function renderInlineBold(text: string) {
  const nodes: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <Box
        key={`b-${match.index}-${match[1]}`}
        component="span"
        sx={{
          fontWeight: 700,
          color: theme.vars.palette.Content.Highlight,
        }}
      >
        {match[1]}
      </Box>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length ? nodes : [text];
}

export default function Analysis_PS_1() {
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom);

  const handleNext = () => {
    navigate(`/program-setup-3/curriculum/1`, { replace: true });
  };

  const trackMixpanel = useMixpanelWeb({
    eventName: "설문 분석 페이지 진입",
    params: {
      event_source: "app",
    },
  });

  const { homeIndicatorHeight } = useGetDeviceInfo();
  const [containerHeight] = useState<number>(
    window.innerHeight - homeIndicatorHeight
  );

  const [analysisDetail, setAnalysisDetail] = useState<
    SurveyAnalysisDetailItem[] | undefined
  >(undefined);

  const [shouldStopPolling, setShouldStopPolling] = useState(false);

  const { data: analysisData, refetch } = useQuery(
    [ONBOARDING_DOMAIN, ONBOARDING_ANALYSIS_DETAIL_ENDPOINT, user?.userId],
    () => onboardingApi.getAnalysisDetail(),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,
      refetchInterval: shouldStopPolling ? false : 1000,
      onSuccess: (data) => {
        if (data && data.result) {
          const result = data.result;

          // analysisList가 없으면 바로 다음 페이지로 이동
          if (result.status === "completed" && !result.analysisList) {
            setShouldStopPolling(true);
            navigate(`/program-setup-3/curriculum/1`, { replace: true });
            return;
          }

          // status가 completed이면 analysisList를 설정하고 polling 중지
          if (result.status === "completed" && result.analysisList) {
            setShouldStopPolling(true);
            setAnalysisDetail(result.analysisList);
            trackMixpanel();
          }
        }
      },
    }
  );

  // status가 completed이면 refetch 중지
  useEffect(() => {
    if (analysisData?.result?.status === "completed") {
      // refetch를 멈추기 위해 enabled를 false로 만듦
    }
  }, [analysisData]);

  const analysisSectionContent = (
    <Stack
      spacing={"40px"}
      sx={{
        width: "100%",
        maxWidth: "100%",
        px: "16px",
        pt: "12px",
        pb: "80px",
      }}
    >
      <Stack
        spacing={"16px"}
        sx={{
          width: "100%",
          maxWidth: "660px",
          textAlign: "left",
          px: "4px",
        }}
      >
        <CustomTypography
          variant={"H1"}
          sx={{
            color: theme.vars.palette.Content.Strong,
            fontWeight: 800,
          }}
        >
          {`${user?.nickname}님의 현재 위치`}
        </CustomTypography>
        <Typography
          variant={"Subtitle"}
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 500,
          }}
        >
          {"설문을 토대로 현재 나의 위치를 확인합니다."}
        </Typography>
      </Stack>

      <Stack
        spacing={"40px"}
        sx={{
          width: "100%",
          maxWidth: "660px",
        }}
      >
        {analysisDetail?.map((item, index) => (
          <Stack
            key={index}
            spacing={"20px"}
            sx={{
              px: "8px",
            }}
          >
            <Stack direction="row" spacing={"8px"} alignItems="center">
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "100px",
                  backgroundColor: theme.vars.palette.Surface.Highlight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    color: theme.vars.palette.Content.Negative,
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </Typography>
              </Box>
              <CustomTypography
                variant={"Body"}
                sx={{
                  p: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {item.title}
              </CustomTypography>
            </Stack>
            <CustomTypography
              variant={"Body"}
              sx={{
                p: 0,
                color: theme.vars.palette.Content.Medium,
                fontWeight: 500,
                whiteSpace: "pre-wrap",
                wordBreak: "keep-all",
              }}
            >
              {renderInlineBold(item.content)}
            </CustomTypography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );

  if (!analysisDetail) {
    return (
      <ContentTemplateV2 noPadding isWide>
        <Stack
          sx={{ width: "100%", minHeight: containerHeight }}
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress color="neutral" />
        </Stack>
      </ContentTemplateV2>
    );
  }

  return (
    <Stack
      sx={{
        paddingBottom: "60px",
      }}
    >
      {analysisSectionContent}
      <BottomNextButton
        disabled={false}
        onClick={handleNext}
        background={theme.vars.palette.Background.Level1}
        text={"다음"}
      />
    </Stack>
  );
}
